// Pure transformations from the raw chart query shape (row 0 = category
// labels, rows 1..n = one series each) into the plot structures the
// components/core/charts/* components render. Extracted from ChartRenderer so
// the logic can be unit-tested without rendering chart.js.

export interface RawPlot {
  id?: number;
  value?: string | number;
  color?: string;
  xAxis?: number;
  yAxis?: number;
  zAxis?: number;
}

export interface RawSeries {
  id?: number;
  vizType?: string;
  headerName?: string;
  color?: string;
  plots?: RawPlot[];
}

export interface RawTab {
  tabTitle?: string;
  chartConfig?: RawSeries[];
}

export interface MetricData {
  title?: string;
  /** A number, or the text already formatted for display (grouping, %). */
  count?: number | string;
  /** A line of context shown under the number. */
  insight?: string;
}

export interface ChartPlot {
  label: string;
  name: string | number | undefined;
  value?: number | string;
  xAxis?: number;
  yAxis?: number;
  zAxis?: number;
  color?: string;
  valueLabel: string;
  symbol: string;
  parentName: null;
  parentLabel: null;
  isPercentage: boolean;
  colorCode: string;
}

export interface ChartSeries {
  id?: number;
  vizType?: string;
  headerName?: string;
  headerValue: string;
  headerSymbol: string;
  colorPaletteCode: string;
  colorPaletteId: null;
  plots: ChartPlot[];
}

export interface TabItem {
  id: number;
  name: string;
  target: string;
  control: string;
  tabId: string;
  content: string;
  data?: RawSeries[];
}

// Chart data may arrive as a JSON string (straight from the query editor) or
// an already-parsed value.
export const parseChartData = <T>(data: T | string | undefined): T | undefined =>
  typeof data === 'string' && data ? (JSON.parse(data.replace(/\n/g, '')) as T) : (data as T | undefined);

const basePlot = (name: ChartPlot['name']): ChartPlot => ({
  label: 'Header',
  name,
  valueLabel: 'Value',
  symbol: 'number',
  parentName: null,
  parentLabel: null,
  isPercentage: false,
  colorCode: 'one',
});

const toSeries = (series: RawSeries, plots: ChartPlot[], vizType = series.vizType): ChartSeries => ({
  id: series.id,
  vizType,
  headerName: series.headerName,
  headerValue: '',
  headerSymbol: '',
  colorPaletteCode: series.color || '',
  colorPaletteId: null,
  plots,
});

const categoryName = (data: RawSeries[], index: number) => data[0].plots?.[index]?.value;

// Maps every series after the header row (row 0) through `mapPlot`.
const mapSeries = (data: RawSeries[], mapPlot: (plot: RawPlot, index: number) => ChartPlot) =>
  data.slice(1).map((series) => ({ series, plots: (series.plots || []).map(mapPlot) }));

interface ValuePlotOptions {
  vizType?: string;
  includeColor?: boolean;
}

// bar / horizontalBar / pie / line
export const toValueSeries = (data: RawSeries[], { vizType, includeColor = false }: ValuePlotOptions = {}): ChartSeries[] =>
  mapSeries(data, (plot, n) => ({
    ...basePlot(categoryName(data, n)),
    value: parseInt(String(plot.value)),
    ...(includeColor && { color: plot.color }),
  })).map(({ series, plots }) => toSeries(series, plots, vizType ?? series.vizType));

// bubble (x/y/z) and scatter (x/y)
export const toAxisSeries = (data: RawSeries[], vizType: 'bubble' | 'scatter'): ChartSeries[] =>
  mapSeries(data, (plot, n) => ({
    ...basePlot(categoryName(data, n)),
    xAxis: plot.xAxis,
    yAxis: plot.yAxis,
    ...(vizType === 'bubble' && { zAxis: plot.zAxis }),
  })).map(({ series, plots }) => toSeries(series, plots, vizType));

// table / tableBar: each row gets two leading "S.No" and blank columns.
const asNumber = (value: unknown): number | null => {
  const text = String(value ?? '').replace(/,/g, '').trim();
  const n = text === '' ? NaN : Number(text);
  return Number.isFinite(n) ? n : null;
};

/** What a table cell shows: a number rounded, text (a name, "12.5%") as it came, or null when there is nothing to show. */
export const tableCellValue = (value: unknown): number | string | null => {
  const n = asNumber(value);
  if (n !== null) return Math.round(n);
  const text = String(value ?? '').trim();
  return text === '' || text === 'NaN' ? null : text;
};

// A numeric cell is truncated to a whole number; a text cell (a name, a percentage) is kept as it came instead of becoming NaN.
const tableCellInput = (value: unknown): number | string => {
  const n = asNumber(value);
  return n !== null ? Math.trunc(n) : String(value ?? '');
};

export const toTableSeries = (data: RawSeries[]): ChartSeries[] =>
  mapSeries(data, (plot, n) => ({ ...basePlot(categoryName(data, n)), value: tableCellInput(plot.value) })).map(
    ({ series, plots }) => ({
      vizType: 'table',
      headerName: series.headerName,
      headerValue: '',
      headerSymbol: '',
      colorPaletteCode: '',
      colorPaletteId: null,
      plots: [{ ...basePlot('S.No'), value: '' }, { ...basePlot(''), value: '' }, ...plots],
    })
  );

const toSlug = (title: string) => title.split(' ').join('-');

export const toTabItems = (data: RawTab[]): TabItem[] =>
  data.flatMap((tab, index) =>
    tab.tabTitle
      ? [
          {
            id: index,
            name: tab.tabTitle,
            target: `#nav-${toSlug(tab.tabTitle)}`,
            control: `nav-${toSlug(tab.tabTitle)}`,
            tabId: `nav-${toSlug(tab.tabTitle)}-tab`,
            content: '',
            data: tab.chartConfig,
          },
        ]
      : []
  );

/**
 * A histogram arrives as one series per bin, the count in headerValue and no plots. Drawn as is, every series is empty, so the
 * bins are folded into one series with a bar per bin. A series that already has plots (histogram comparison) is left alone.
 */
export const foldBinSeries = (series: ChartSeries[]): ChartSeries[] => {
  const bins = series.filter((s) => s.plots.length === 0 && s.headerName !== undefined && s.headerValue !== '' && s.headerValue !== undefined);
  if (bins.length === 0 || bins.length !== series.length) return series;
  const plots = bins.map((s) => ({ ...basePlot(s.headerName), value: Number(String(s.headerValue).replace(/,/g, '')) || 0 }));
  return [{ ...bins[0], headerName: '', headerValue: '', plots }];
};
